import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Paper } from '../../../../../components/mui/Core/index'
import { useTheme } from '../../../../../components/mui/Styles/index'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setSelectedOrder } from '../../../hold-allocation-slice'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

const holdStatusColor = (status) => {
  if (status === 'ON_HOLD') return { bg: '#fdecea', color: '#cc3f0c' }
  if (status === 'PARTIAL_HOLD') return { bg: '#fff8e1', color: '#e65100' }
  return { bg: '#e8f5e9', color: '#2a7b5a' }
}

function HoldOrderTable({ orders, isFetching }) {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleRowClick = (params) => {
    dispatch(setSelectedOrder(params.row))
    navigate(`/hold-allocation/${params.row.orderId}`)
  }

  const rows = orders.map((o) => ({
    id: o.orderId,
    orderId: o.orderId,
    dmsOrderNumber: o.dmsOrderNumber,
    poNumber: o.poNumber || 'N/A',
    shipToName: o.shipToName,
    holdStatus: o.holdStatus,
    holdDisplay: o.holdDisplay || '—',
    openValue: o.openValue,
    openLines: o.openLines,
    orderDate: o.orderDate,
    bucket: o.bucket,
  }))

  const columns = [
    {
      field: 'orderId',
      headerName: 'Order ID',
      flex: 0.5,
      minWidth: 100,
      renderCell: (p) => (
        <Box
          sx={{
            color: '#2db47d',
            fontFamily: 'AtlasGroteskRegular',
            fontSize: '0.85rem',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {p.value}
        </Box>
      ),
    },
    { field: 'dmsOrderNumber', headerName: 'DMS Order', flex: 0.6, minWidth: 120 },
    { field: 'poNumber', headerName: 'PO #', flex: 0.5, minWidth: 110 },
    { field: 'shipToName', headerName: 'Ship To', flex: 1, minWidth: 180 },
    {
      field: 'holdStatus',
      headerName: 'Hold Status',
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      renderCell: (p) => {
        const { bg, color } = holdStatusColor(p.value)
        return (
          <Box
            sx={{
              backgroundColor: bg,
              color,
              borderRadius: 1,
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
              fontFamily: 'AtlasGroteskRegular',
              whiteSpace: 'nowrap',
            }}
          >
            {p.value?.replace(/_/g, ' ')}
          </Box>
        )
      },
    },
    {
      field: 'holdDisplay',
      headerName: 'Hold Codes',
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {p.value && p.value !== '—'
            ? p.value.split(',').map((code) => (
                <Box
                  key={code}
                  sx={{
                    backgroundColor: '#fdecea',
                    color: '#cc3f0c',
                    borderRadius: 1,
                    px: 0.75,
                    fontSize: '0.7rem',
                    fontFamily: 'AtlasGroteskRegular',
                    border: '1px solid #f5c0b0',
                  }}
                >
                  {code.trim()}
                </Box>
              ))
            : <Box sx={{ color: '#b7b7b7', fontSize: '0.75rem' }}>—</Box>}
        </Box>
      ),
    },
    {
      field: 'openValue',
      headerName: 'Open Value',
      flex: 0.5,
      minWidth: 110,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—',
    },
    {
      field: 'openLines',
      headerName: 'Lines',
      flex: 0.25,
      minWidth: 70,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'orderDate',
      headerName: 'Order Date',
      flex: 0.6,
      minWidth: 140,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString('en-US') : '—',
    },
  ]

  const gridStyle = {
    minHeight: 160,
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .MuiDataGrid-cell': { paddingLeft: '16px' },
    '& .MuiDataGrid-columnHeader': { paddingLeft: '16px' },
  }

  return (
    <Paper>
      <DataGrid
        sx={gridStyle}
        rows={rows}
        columns={columns}
        loading={isFetching}
        pageSizeOptions={[25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        onRowClick={handleRowClick}
        slots={{ noRowsOverlay: NoResultsFoundTableOverlay }}
        slotProps={{ noRowsOverlay: { filterByDescription: '', noResultsValue: '' } }}
        disableColumnFilter
        disableRowSelectionOnClick
        autoHeight
      />
    </Paper>
  )
}

HoldOrderTable.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFetching: PropTypes.bool.isRequired,
}

export default HoldOrderTable
